const $$splitComponentImporter = () => import('/home/runner/work/zngly-corporate/zngly-corporate/wp-content/themes/zngly-corporate/frontend/src/routes/dash/settings/management/users/$user/route.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
import UserListSelect from '@/Components/select/users/UserSelect';
import { requireUserCaps } from '@/Router';
import { Analytics } from '@/Telemetry';
import { useUserCaps } from '@/Utility/auth/Auth';
import { Link, useNavigate } from '@/Utility/hooks';
import { sendEmail } from '@/graphql/mutations/EmailSender.mutation';
import { sendPasswordResetEmailMutation, useDeleteUserMutation, useUpdateUserMutation } from '@/graphql/mutations/User.mutation';
import { UserManagementSingleQuery, type User_Management_Single_Query, type User_Management_Single_Query$data } from '@/graphql/queries/User.query';
import { RelayEnvironment } from '@/graphql/relay-env';
import { faArrowLeft, faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { EditOff, ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Button, CircularProgress, Divider, Grid, MenuItem, Skeleton, Slide, Stack, TextField, type TextFieldProps, Typography } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { format as dateFormat } from 'date-fns';
import { isEqual } from 'lodash';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { loadQuery } from 'react-relay';
import slugify from 'react-slugify';
import { toast } from 'react-toastify';
import { type UserFormData, type UserPasswordSchema, userPasswordRefinedSchema, userRefinedSchema } from '../-lib/utils';
type Props = {
  data?: User_Management_Single_Query$data;
  isFallback?: boolean;
};
const UserPage = (props: Props) => {
  const defaultValues: Partial<UserFormData> = useMemo(() => {
    return {
      email: props?.data?.user?.email ?? '',
      firstName: props?.data?.user?.firstName ?? '',
      lastName: props?.data?.user?.lastName ?? '',
      role: props?.data?.user?.role as UserFormData['role'] ?? 'user',
      userName: props?.data?.user?.username ?? '',
      slug: props?.data?.user?.slug ?? '',
      jobRole: props?.data?.user?.meta?.jobRole ?? ''
    };
  }, [props?.data?.user]);
  const {
    control,
    register,
    handleSubmit,
    watch,
    clearErrors,
    setValue,
    reset,
    formState: {
      errors,
      touchedFields,
      isValid
    }
  } = useForm<Omit<UserFormData, 'password' | 'confirmPassword'>>({
    resolver: zodResolver(userRefinedSchema),
    defaultValues: defaultValues
  });
  const passwordForm = useForm<UserPasswordSchema>({
    resolver: zodResolver(userPasswordRefinedSchema),
    defaultValues: {
      password: '',
      confirmPassword: ''
    }
  });
  const allWatchedFormValues = watch();
  const [isFormChanged, setIsFormChanged] = useState(false);
  useEffect(() => {
    // Trigger revalidation so any form content changes will trigger a revalidation
    if (!isEqual(defaultValues, allWatchedFormValues)) {
      setIsFormChanged(true);
    } else {
      setIsFormChanged(false);
    }
  }, [allWatchedFormValues]);
  const [updateUser, isUpdatingUser] = useUpdateUserMutation();
  const {
    navigateBack
  } = useNavigate();
  const canEdit = useUserCaps(['edit_users']);
  const avatarStr = useMemo(() => {
    if (props?.isFallback) {
      return null;
    }
    let str = '';
    if (props?.data?.user?.firstName) {
      str += props?.data?.user?.firstName[0].toUpperCase();
    }
    if (props?.data?.user?.lastName) {
      str += props?.data?.user?.lastName[0].toUpperCase();
    }
    if (str === '' && props?.data?.user?.email) {
      str = props?.data?.user?.email[0].toUpperCase();
    }
    return str;
  }, [props?.data, props?.isFallback]);
  const onSubmit = (data: Omit<UserFormData, 'password' | 'confirmPassword'>) => {
    if (isUpdatingUser) {
      toast.warning('User update in progress');
      return;
    }
    if (!props?.data?.user?.id) {
      return;
    }
    updateUser({
      input: {
        id: props?.data?.user?.id!,
        firstName: data.firstName,
        lastName: data.lastName,
        roles: [data.role],
        nicename: data.slug,
        displayName: [data.firstName, data.lastName].filter(x => !!x).join(' '),
        jobRole: data.jobRole
      }
    }, {
      onCompleted: (response, errors) => {
        if (!!errors?.length) {
          toast.error(errors.map(err => err.message).join(', '));
          return;
        }
        Analytics.userPush('user_dash_action', {
          eventaction: 'update_user',
          value: props?.data?.user?.email
        });
        toast.success(`${props?.data?.user?.email ?? ''} Updated`);
        navigateBack('/dash/settings/management/users');
      }
    });
  };
  const onPasswordResetHandler = useCallback(async () => {
    if (!props?.data?.user?.username) {
      return;
    }

    // prompt user to confirm
    if (!window.confirm('Are you sure you want to send a password reset email?')) {
      return;
    }
    sendPasswordResetEmailMutation({
      input: {
        username: props?.data?.user?.username
      }
    }, {
      onCompleted: (response, errors) => {
        if (errors?.length) {
          toast.error(errors.map(err => err.message).join(', '));
          return;
        }
        if (response?.sendPasswordResetEmail?.success) toast.success(`Password reset successfully: ${props?.data?.user?.email}`);else toast.error('Password reset failed');
      }
    });
  }, [props?.data?.user?.username]);
  const onPasswordUpdateHandler = useCallback((data: UserPasswordSchema) => {
    if (!props?.data?.user?.id) {
      return;
    }
    updateUser({
      input: {
        id: props?.data?.user?.id!,
        password: data.password
      }
    }, {
      onCompleted: (response, errors) => {
        if (!!errors?.length) {
          toast.error(errors.map(err => err.message).join(', '));
          return;
        }
        Analytics.userPush('user_dash_action', {
          eventaction: 'update_user_password',
          value: props?.data?.user?.email
        });
        const domain = window.location.origin;
        const prettyDomain = domain.replace('https://', '').replace('http://', '');
        sendEmail({
          input: {
            type: 'HTML',
            to: [props?.data?.user?.email],
            from: `Zngly <no-reply@${prettyDomain}>`,
            subject: `Zngly: Password Update ${prettyDomain}`,
            body: /* html */`
                <p>Hi ${props?.data?.user?.firstName},</p>
                <br />
                <p>Your zngly password has updated for <strong><a href="${domain}">${prettyDomain}</a></strong>.</p>
                <p>Click <a href="${domain}/auth/login">here</a> to login.</p>
                <br />
                <p>If you did not request this change, please contact support.</p>
                <br />
                <p>Thanks,</p>
                <p>The Zngly Team</p>
              `
          }
        });
        toast.success(`${props?.data?.user?.email ?? ''} Password Updated`);
        passwordForm.reset();
      }
    });
  }, [props?.data]);
  const [deleteUserMutation, isDeletingUser] = useDeleteUserMutation();
  const onDeleteUserHandler = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const attributeTo = formData.get('attribute-to') as string;

    // prompt user to confirm
    if (!window.confirm(`Are you sure you want to delete ${props?.data?.user?.email ?? ''}?`)) {
      return;
    }
    if (!props?.data?.user?.id) {
      return;
    }
    deleteUserMutation({
      input: {
        id: props?.data?.user?.id!,
        ...(!!attributeTo?.length && {
          reassignId: attributeTo
        })
      }
    }, {
      onCompleted(response, errors) {
        if (!!errors?.length) {
          toast.error(errors.map(err => err.message).join(', '));
          return;
        }
        Analytics.userPush('user_dash_action', {
          eventaction: 'delete_user',
          value: props?.data?.user?.email
        });
        toast.success(`${props?.data?.user?.email ?? ''} Deleted`);
        navigateBack('/dash/settings/management/users');
      }
    });
  }, [props?.data?.user?.id]);
  const uiDisabled = useMemo(() => {
    return !canEdit || isUpdatingUser || props?.isFallback || isDeletingUser;
  }, [canEdit, isUpdatingUser, props?.isFallback, isDeletingUser]);
  return <Grid container sx={{
    flex: 1,
    pt: 1,
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    maxHeight: 'min(70vh, 900px)'
  }} size={{
    sm: 12
  }}>
      <Grid size={{
      xs: 12,
      md: 3
    }} sx={{
      borderRight: '1px solid #e0e0e0',
      pr: 2,
      pl: 1,
      overflowY: 'auto',
      height: '100%',
      pb: isFormChanged ? 10 : 2,
      transition: 'padding-bottom 0.3s'
    }}>
        {/* avatar and email */}
        <Stack direction="column" spacing={0.5} sx={{
        position: 'sticky',
        top: 0,
        pb: 1,
        zIndex: 1,
        backgroundColor: 'white'
      }}>
          <Typography variant="caption" fontSize={'0.85rem'}>
            <Link isBack to="/dash/settings/management/users" style={{
            color: 'inherit'
          }}>
              <FontAwesomeIcon icon={faArrowLeft} size="sm" style={{
              cursor: 'pointer'
            }} /> Users
            </Link>
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={props?.data?.user?.meta?.profileImage?.mediaItemUrl ?? undefined}>{avatarStr}</Avatar>

            <Typography variant="body1" sx={{
            display: 'flex',
            alignItems: 'center'
          }}>
              {props?.data?.user?.email?.toLowerCase?.() || <Skeleton width={100} />}
              <FontAwesomeIcon icon={faCopy} size="sm" style={{
              marginLeft: '8px',
              cursor: 'pointer',
              ...(props?.isFallback && {
                height: '0px',
                width: '0px'
              })
            }} onClick={() => {
              if (!props?.data?.user?.email) return;
              navigator.clipboard.writeText(props?.data?.user?.email?.toLowerCase?.() ?? '').then(() => {
                toast.success(`${props?.data?.user?.email?.toLowerCase?.()} copied to clipboard`);
              });
            }} />
            </Typography>
          </Stack>
        </Stack>

        {/* form fill */}

        <Stack spacing={1}>
          <CustomTextField fallback={props?.isFallback} label="First Name" {...register('firstName')} error={!!errors.firstName} helperText={errors.firstName?.message} />

          <CustomTextField fallback={props?.isFallback} label="Last Name" {...register('lastName')} error={!!errors.lastName} helperText={errors.lastName?.message} />

          <CustomTextField fallback={props?.isFallback} label="Job Title" {...register('jobRole')} error={!!errors.jobRole} helperText={errors.jobRole?.message} />

          <CustomTextField fallback={props?.isFallback} label="Username" {...register('userName')} readOnly error={!!errors.userName} helperText={errors.userName?.message} />

          <CustomTextField fallback={props?.isFallback} label="Slug" {...register('slug')} onBlur={e => {
          const slugified = slugify(e.target.value);
          if (e.target.value !== slugified) {
            setValue('slug', slugified);
          }
          e.target.value = slugified;
          register('slug').onBlur(e);
        }} error={!!errors.slug} helperText={errors.slug?.message} />

          <Controller control={control} name="role" render={({
          field
        }) => <CustomTextField select fallback={props?.isFallback} label="Role" {...field} error={!!errors.role} helperText={errors.role?.message}>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="user_sales">Sales User</MenuItem>
                <MenuItem value="user_sales_restricted">Sales User (Restricted)</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </CustomTextField>} />
        </Stack>
      </Grid>
      <Grid size={{
      sm: 12,
      md: 9
    }} sx={{
      pt: 2,
      pl: 2,
      pr: 2,
      overflowY: 'auto',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      pb: isFormChanged ? 10 : 2,
      transition: 'padding-bottom 0.3s'
    }}>
        <Accordion defaultExpanded variant="outlined">
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="body1">User Details</Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Typography variant="body2" sx={{
            mb: 1
          }}>
              Registered:{' '}
              {props.data?.user?.registeredDate ? dateFormat(new Date(props.data.user.registeredDate), 'EEE do MMM yy HH:mm:ss') : 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{
            mb: 1
          }}>
              Display Name: {props?.data?.user?.name}
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion variant="outlined">
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="body1">Password</Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Typography variant="body2" sx={{
            mb: 1
          }}>
              Update Password
            </Typography>

            <form style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: '100%'
          }} onSubmit={passwordForm.handleSubmit(onPasswordUpdateHandler)}>
              <TextField {...passwordForm.register('password')} type="password" label="Password" disabled={uiDisabled} error={!!passwordForm.formState.errors.password && passwordForm.formState.touchedFields.password} helperText={passwordForm.formState.errors.password?.message} autoComplete="new-password" />

              <TextField {...passwordForm.register('confirmPassword')} type="password" label="Confirm Password" disabled={uiDisabled} error={!!passwordForm.formState.errors.confirmPassword && passwordForm.formState.touchedFields.confirmPassword} helperText={passwordForm.formState.errors.confirmPassword?.message} />

              <Button className="z-btn-1" disabled={uiDisabled} type="submit" variant="outlined" fullWidth>
                Update Password
              </Button>
            </form>

            <Divider sx={{
            mt: 2,
            mb: 2
          }} />

            <Typography variant="body2" sx={{
            mb: 1
          }}>
              Send Password Reset Email
            </Typography>
            <Button className="z-btn-1" fullWidth disabled={uiDisabled} onClick={onPasswordResetHandler}>
              Send Password Reset Email
            </Button>
          </AccordionDetails>
        </Accordion>

        <Accordion variant="outlined">
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="body1">Delete User</Typography>
          </AccordionSummary>

          <AccordionDetails>
            <form onSubmit={onDeleteUserHandler}>
              <Typography variant="body2" sx={{
              mb: 1
            }}>
                {`What should be done with content owned by ${props?.data?.user?.email ?? 'N/A'}?`}
              </Typography>

              <Stack spacing={1} direction="row" sx={{
              width: '100%',
              mb: 1
            }} alignItems="center">
                <UserListSelect queryProps={{
                ...(props?.data?.user?.username && {
                  defaultVars: {
                    where: {
                      loginNotIn: [props?.data?.user?.username]
                    }
                  }
                })
              }} textProps={{
                label: 'Attribute all content to',
                helperText: "Leave blank to delete all the user's content"
              }} selectProps={{
                disabled: uiDisabled,
                onChange: (e, value) => {
                  document.getElementById('user-list-select-attribute-to')?.setAttribute('value', value?.id ?? '');
                }
              }} />
                <input type="hidden" name="attribute-to" id="user-list-select-attribute-to" />
              </Stack>

              {/* attribute to another user */}
              <Button className="z-btn-1 red" type="submit" fullWidth disabled={uiDisabled}>
                Delete User
              </Button>
            </form>
          </AccordionDetails>
        </Accordion>
      </Grid>

      {/* save box */}
      <Slide direction="up" in={isFormChanged && canEdit} mountOnEnter unmountOnExit timeout={300}>
        <Box sx={{
        height: 60,
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 1,
        pr: '16px !important',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        zIndex: 1
      }}>
          <Button className="z-btn-4" disabled={isUpdatingUser} onClick={() => {
          clearErrors();
          reset(defaultValues);
        }}>
            Cancel
          </Button>
          <Button loading={isUpdatingUser} disabled={canEdit === false} onClick={handleSubmit(onSubmit)} variant="outlined">
            Save
          </Button>
        </Box>
      </Slide>
    </Grid>;
};
const CustomTextField = forwardRef<HTMLDivElement, TextFieldProps & {
  fallback?: boolean;
  readOnly?: boolean;
  isLoading?: boolean;
}>((props, ref) => {
  const canEdit = useUserCaps(['edit_users']);
  if (props?.fallback === true) {
    return <Skeleton width={'100%'} sx={{
      mt: 1,
      mb: 1
    }} />;
  }
  return <TextField variant="standard" {...props} ref={ref} disabled={!canEdit} sx={{
    ...(props?.sx ?? {}),
    ...(props?.readOnly === true && {
      cursor: 'not-allowed',
      pointerEvents: 'none'
    })
  }} slotProps={{
    ...props.slotProps,
    input: {
      ...props.slotProps?.input,
      readOnly: !canEdit || props?.readOnly === true,
      ...(props?.isLoading && {
        endAdornment: <CircularProgress size={20} color="inherit" />
      }),
      ...(props?.readOnly && {
        endAdornment: <EditOff fontSize="small" />
      })
    }
  }}>
      {props?.children}
    </TextField>;
});
export const Route = createFileRoute('/dash/settings/management/users/$user')({
  component: lazyRouteComponent($$splitComponentImporter, 'component', () => Route.ssr),
  pendingComponent: () => <UserPage isFallback />,
  beforeLoad: () => {
    requireUserCaps(['list_users'], {
      to: '/dash/settings/management/profile'
    });
  },
  loader: ({
    params
  }) => {
    return loadQuery<User_Management_Single_Query>(RelayEnvironment, UserManagementSingleQuery, {
      userId: params.user
    }, {
      fetchPolicy: 'store-or-network'
    });
  }
});